import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  DataGridPro,
  type DataGridProProps,
  type GridEventListener,
  type GridRowId, type GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { Card } from '@mui/material'
import WrappedGridToolbar, { type WrappedGridToolbarProps } from './wrapped-data-grid-toolbar'
import { type WrappedDataGridProps } from './wrapped-data-grid-types'

// augment the props for the toolbar slot
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends WrappedGridToolbarProps {
    // intentionally left empty
  }
}

const groupingColDef: DataGridProProps['groupingColDef'] = {
  headerName: '', // Hide group column display name
  hideDescendantCount: true,
  valueFormatter: () => '', // Hide group column value and show only the toggle button
  width: 60
}

const WrappedDataGrid: React.FC<WrappedDataGridProps> = (
  {
    gridData, stableCoinVisibility, gridMode, toolbarData,
    styleData, mutate,
  },
) => {
  const portfolio = gridData.portfolio
  const csvOptions = gridData.csvOptions

  const apiRef = useGridApiRef()
  const expansionLookup = useRef<Record<GridRowId, boolean>>({})

  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([])

  useEffect(() => {
    setGridSortModel(gridData.sortModel ?? [])
    apiRef.current.subscribeEvent('rowExpansionChange', (node) => {
      expansionLookup.current[node.id] = node.childrenExpanded as boolean
    })
  }, [apiRef])

  const isGroupExpandedByDefault = useCallback(
    (node: { id: string | number }) => {
      return expansionLookup.current[node.id]
    },
    [expansionLookup]
  )

  const onRowClick = useCallback<GridEventListener<'rowClick'>>(
    (params) => {
      const rowNode = apiRef.current.getRowNode(params.id)
      if (rowNode != null && rowNode.type === 'group') {
        const expandState = rowNode.childrenExpanded === undefined || !rowNode.childrenExpanded
        apiRef.current.setRowChildrenExpansion(params.id, expandState)

        // Ignored IIFE to resize all columns only after the row expansion change has been applied to the DOM
        void (async function () {
          await apiRef.current.autosizeColumns(gridData.autosizeOptions)
        })()
      }
    }, [apiRef, gridData.autosizeOptions])

  useEffect(() => {
    // Ignored IIFE to resize all columns
    void (async function () {
      await apiRef.current.autosizeColumns(gridData.autosizeOptions)
    })()
  }, [gridData.rows])

  return (
    <Card
      variant='outlined'
      sx={{
        width: 1,
        height: 1,
        padding: 1,
        background: 'transparent',
        border: 0,
        '& .text.positive': {
          color: 'success.main'
        },
        '& .text.negative': {
          color: 'error.main'
        }
      }}>
      <DataGridPro
        treeData
        disableRowSelectionOnClick
        apiRef={apiRef}
        onRowClick={onRowClick}
        isGroupExpandedByDefault={isGroupExpandedByDefault}
        groupingColDef={groupingColDef}
        autosizeOptions={gridData.autosizeOptions}
        sortModel={gridSortModel}
        onSortModelChange={(model) => { setGridSortModel(model) }}
        getTreeDataPath={(row) => row.path}
        autoHeight={true}
        columnHeaderHeight={40}
        rowHeight={20}
        hideFooter={true}
        slots={{ toolbar: WrappedGridToolbar }}
        slotProps={{
          toolbar: {
            gridMode,
            stableCoinVisibility,
            mutate,
            portfolio,
            toolbarData,
            csvOptions
          }
        }}
        rows={gridData.rows}
        columns={gridData.columns}
        sx={{
          ...styleData,
          '& .MuiDataGrid-container--top [role=row]': {
            background: 'transparent' // remove inherited container background
          },
          '& .MuiDataGrid-container--bottom [role=row]': {
            background: 'transparent' // remove inherited container background
          },
          '& .MuiDataGrid-treeDataGroupingCellToggle > button': {
            padding: 0,
            marginTop: '-4px', // visually realign
          }
        }}
      />
    </Card>
  )
}

export default WrappedDataGrid
